import * as turf from '@turf/turf';

interface Point2D {
  x: number;
  y: number;
}

type SimplifyLv95PolygonOptions = {
  tolerance?: number;
};

const DEFAULT_SIMPLIFICATION_TOLERANCE = 0.02;

const distancePointToSegment = (point: Point2D, segmentStart: Point2D, segmentEnd: Point2D): number => {
  const dx = segmentEnd.x - segmentStart.x;
  const dy = segmentEnd.y - segmentStart.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return Math.hypot(point.x - segmentStart.x, point.y - segmentStart.y);
  }
  const t = Math.max(
    0,
    Math.min(1, ((point.x - segmentStart.x) * dx + (point.y - segmentStart.y) * dy) / lengthSquared)
  );
  const projection = {
    x: segmentStart.x + t * dx,
    y: segmentStart.y + t * dy,
  };
  return Math.hypot(point.x - projection.x, point.y - projection.y);
};

const normalizeAngleDegrees = (angle: number): number => {
  const normalized = angle % 360;
  return normalized < 0 ? normalized + 360 : normalized;
};

const areBorderCoordinatesEqual = (
  left: Point2D[] | null | undefined,
  right: Point2D[] | null | undefined
): boolean => {
  if (left === right) {
    return true;
  }
  if (!left || !right || left.length !== right.length) {
    return false;
  }
  return left.every((coord, index) => coord.x === right[index]?.x && coord.y === right[index]?.y);
};

const areCoordinatesEqual = (
  first: Partial<Point2D> | null | undefined,
  second: Partial<Point2D> | null | undefined
): boolean => Number(first?.x) === Number(second?.x) && Number(first?.y) === Number(second?.y);

const getSignedArea = (coordinates: Point2D[] = []): number => {
  if (coordinates.length < 3) {
    return 0;
  }

  let area = 0;
  for (let index = 0; index < coordinates.length; index += 1) {
    const current = coordinates[index];
    const next = coordinates[(index + 1) % coordinates.length];
    area += current.x * next.y - next.x * current.y;
  }

  return area / 2;
};

const normalizeLv95PolygonCoordinates = (
  coordinates: ReadonlyArray<readonly [number, number] | readonly number[]> = []
): Point2D[] => {
  if (!Array.isArray(coordinates) || coordinates.length < 3) {
    return [];
  }

  const normalized = coordinates
    .map((coordinate) => ({
      x: Number(coordinate?.[0]),
      y: Number(coordinate?.[1]),
    }))
    .filter((coordinate) => Number.isFinite(coordinate.x) && Number.isFinite(coordinate.y));

  const deduplicated = normalized.filter((coordinate, index) => {
    if (index === 0) {
      return true;
    }
    return !areCoordinatesEqual(coordinate, normalized[index - 1]);
  });

  if (deduplicated.length >= 2 && areCoordinatesEqual(deduplicated[0], deduplicated[deduplicated.length - 1])) {
    deduplicated.pop();
  }

  return deduplicated;
};

const simplifyLv95PolygonCoordinates = (
  coordinates: ReadonlyArray<Point2D> = [],
  { tolerance = DEFAULT_SIMPLIFICATION_TOLERANCE }: SimplifyLv95PolygonOptions = {}
): Point2D[] => {
  if (!Array.isArray(coordinates) || coordinates.length < 3) {
    return [...coordinates];
  }

  const normalized = coordinates.map((coordinate) => ({ x: Number(coordinate.x), y: Number(coordinate.y) }));
  if (normalized.some((coordinate) => !Number.isFinite(coordinate.x) || !Number.isFinite(coordinate.y))) {
    return [...coordinates];
  }

  try {
    const ring = [...normalized.map(({ x, y }) => [x, y]), [normalized[0].x, normalized[0].y]];
    const simplifiedRing = turf.simplify(turf.polygon([ring]), { tolerance, highQuality: true }).geometry
      .coordinates[0];
    const simplified = simplifiedRing.slice(0, -1).map(([x, y]) => ({ x, y }));
    return simplified.length >= 3 ? simplified : [...coordinates];
  } catch {
    return [...coordinates];
  }
};

const extractLargestLv95RingCoordinates = (geometry: GeoJSON.Geometry | null | undefined): Point2D[] => {
  if (!geometry) {
    return [];
  }

  const rings =
    geometry.type === 'Polygon'
      ? [geometry.coordinates?.[0] ?? []]
      : geometry.type === 'MultiPolygon'
        ? geometry.coordinates.map((polygon) => polygon?.[0] ?? [])
        : [];

  const normalizedRings = rings.map((ring) => normalizeLv95PolygonCoordinates(ring)).filter((ring) => ring.length >= 3);

  if (!normalizedRings.length) {
    return [];
  }

  return normalizedRings.reduce((largestRing, currentRing) =>
    Math.abs(getSignedArea(currentRing)) > Math.abs(getSignedArea(largestRing)) ? currentRing : largestRing
  );
};

const isPointInsideBounds = (
  latlng: { lat: number; lng: number } | null | undefined,
  bounds: [[number, number], [number, number]] | null | undefined
): boolean => {
  if (!latlng || !bounds || bounds.length !== 2) return false;
  const [[minLat, minLng], [maxLat, maxLng]] = bounds;
  return latlng.lat >= minLat && latlng.lat <= maxLat && latlng.lng >= minLng && latlng.lng <= maxLng;
};

export {
  distancePointToSegment,
  normalizeAngleDegrees,
  areBorderCoordinatesEqual,
  areCoordinatesEqual,
  normalizeLv95PolygonCoordinates,
  simplifyLv95PolygonCoordinates,
  extractLargestLv95RingCoordinates,
  isPointInsideBounds,
};
export type { Point2D };
